import AddIcon from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DataGrid, type GridColDef, type GridRowParams } from '@mui/x-data-grid';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { mediaNetworkApi } from '@/api/media-network/mediaNetworkApi';
import { mediaNetworkKeys } from '@/api/media-network/mediaNetworkQueries';
import { type Playlist } from '@/models/media-network/Playlist';
import { appTypeLabel } from '@/models/media-network/appLabels';
import { useAppContext } from '../../AppContext';
import { useLocalizedStrings } from '../../localization/LocalizedStringsProvider';
import { usePermissions } from '../../permissions/usePermissions';
import { Perm } from '../../permissions/permissions';
import DataGridDeleteButton from '../common/data-grid/DataGridDeleteButton';
import DataGridEditButton from '../common/data-grid/DataGridEditButton';
import { useToast } from '../common/toast/ToastProvider';
import PlaylistDialog from './dialogs/PlaylistDialog';
import PlaylistItemGrid from './PlaylistItemGrid';

// Master-detail on one screen: pick a playlist above, manage its media below. Replaces the old drill-down
// into a separate editor page — the two grids are always the same two things, and a page transition to see
// four rows of media wasn't earning itself.
const Playlists = () => {
    const qc = useQueryClient();
    const toast = useToast();
    const strings = useLocalizedStrings();
    const app = useAppContext();
    const perms = usePermissions();
    const canCreate = perms.canCreate(Perm.mediaNetworkAdBanner);
    const canUpdate = perms.canUpdate(Perm.mediaNetworkAdBanner);
    const canDelete = perms.canDelete(Perm.mediaNetworkAdBanner);

    const brandId = app.brandId ?? undefined;

    const [selectedId, setSelectedId] = useState<string | null>(null);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editing, setEditing] = useState<Playlist | null>(null);

    const { data: playlists, isPending } = useQuery<Playlist[]>({
        queryKey: mediaNetworkKeys.list(brandId ?? ''),
        enabled: !!brandId,
        queryFn: () => mediaNetworkApi.list(brandId!),
        staleTime: 0,
    });

    // Keep a sane selection as the list changes: default to the first playlist, and drop a selection whose
    // row no longer exists (deleted here, or the brand switched underneath us).
    useEffect(() => {
        if (!playlists?.length) {
            setSelectedId(null);
            return;
        }
        if (!selectedId || !playlists.some((p) => p.id === selectedId)) {
            setSelectedId(playlists[0].id);
        }
    }, [playlists, selectedId]);

    const selected = playlists?.find((p) => p.id === selectedId) ?? null;

    const { mutateAsync: remove } = useMutation({
        mutationFn: (id: string) => mediaNetworkApi.remove(id),
        onSuccess: async () => {
            toast({ severity: 'success', message: strings.playlistDeleted });
            await qc.invalidateQueries({ queryKey: mediaNetworkKeys.list(brandId ?? '') });
        },
        onError: () => toast({ severity: 'error', message: strings.playlistDeleteError }),
    });

    const columns: GridColDef<Playlist>[] = [
        { field: 'name', headerName: strings.name, flex: 2 },
        {
            field: 'apps', headerName: strings.playlistShowsOn, flex: 2, sortable: false,
            renderCell: (params) => (
                <Stack direction="row" spacing={0.5} sx={{ alignItems: 'center', height: '100%' }}>
                    {params.row.apps.map((app) => <Chip key={app} size="small" label={appTypeLabel(app)} />)}
                </Stack>
            ),
        },
        {
            // Its own column rather than a marker on the name: it's the first thing to check when someone
            // asks why a promo isn't showing, and a column can be scanned down.
            field: 'enabled', headerName: strings.enabled, flex: 1,
            renderCell: (params) => (
                <Stack direction="row" sx={{ alignItems: 'center', height: '100%' }}>
                    <Chip
                        size="small"
                        variant="outlined"
                        color={params.row.enabled ? 'success' : 'default'}
                        label={params.row.enabled ? strings.enabled : strings.disabled}
                    />
                </Stack>
            ),
        },
        {
            field: 'actions', headerName: strings.actions, type: 'actions', flex: 1,
            getActions: (params: GridRowParams<Playlist>) => [
                <DataGridEditButton
                    id={params.id.toString()}
                    onClick={() => { setEditing(params.row); setDialogOpen(true); }}
                    aria-label="edit playlist"
                    readOnly={!canUpdate}
                />,
                <DataGridDeleteButton
                    id={params.id.toString()}
                    getWarningMessage={(id) => strings.playlistDeleteConfirm.replace('{{name}}', playlists?.find(p => p.id === id)?.name ?? '')}
                    onClick={(id) => remove(id)}
                    aria-label="delete playlist"
                    hidden={!canDelete}
                />,
            ],
        },
    ];

    if (!brandId) {
        return <Typography variant="body2" color="text.secondary">{strings.playlistSelectBrandTopBar}</Typography>;
    }

    return (
        // Side by side, so the playlist you picked stays in view next to its media rather than scrolling off
        // the top. Stacks vertically below lg, where two grids side by side would leave neither readable.
        <Stack
            direction={{ xs: 'column', lg: 'row' }}
            spacing={3}
            sx={{ height: '100%', minHeight: 0 }}
        >
            <Stack spacing={1} sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                    <Typography variant="h6">{strings.playlists}</Typography>
                    {canCreate && (
                        <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => { setEditing(null); setDialogOpen(true); }}
                            size="input"
                        >
                            {strings.playlistNew}
                        </Button>
                    )}
                </Stack>

                <Box sx={{ flex: 1, minHeight: 300 }}>
                    <DataGrid
                        rows={playlists ?? []}
                        columns={columns}
                        loading={isPending}
                        getRowId={(row) => row.id}
                        disableColumnMenu
                        // Selection drives the Items grid alongside, so the row is the control.
                        rowSelectionModel={selectedId ? { type: 'include', ids: new Set([selectedId]) } : undefined}
                        onRowClick={(params) => setSelectedId(params.id.toString())}
                        sx={{ '& .MuiDataGrid-row': { cursor: 'pointer' } }}
                    />
                </Box>
            </Stack>

            <Stack sx={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                {selected
                    ? <PlaylistItemGrid playlist={selected} brandId={brandId} canUpdate={canUpdate} canDelete={canDelete} />
                    : <Typography variant="body2" color="text.secondary">{strings.playlistSelectOne}</Typography>}
            </Stack>

            <PlaylistDialog
                open={dialogOpen}
                brandId={brandId}
                playlist={editing}
                readOnly={!!editing && !canUpdate}
                onClose={() => setDialogOpen(false)}
            />
        </Stack>
    );
};

export default Playlists;
